import { MediaDisplay } from './MediaDisplay';

/**
 * LinkedInMockup - LinkedIn Single Image or Video Ad Mockup
 *
 * Note: imageUrl prop can contain either image or video URLs
 */

interface LinkedInMockupProps {
  adType?: 'square' | 'landscape';
  profilePicUrl?: string;
  pageName?: string;
  caption?: string;
  imageUrl?: string;
  tagline?: string;
  website?: string;
  ctaText?: string;
}

const reactionIcons: Record<string, string> = {
  like: '/icons/linkedin-like.svg',
  celebrate: '/icons/linkedin-clap.svg',
  love: '/icons/linkedin-heart.svg',
};

const reactionLabels: Record<string, string> = {
  like: 'Like',
  celebrate: 'Celebrate',
  love: 'Love',
};

export function LinkedInMockup({
  adType = 'square',
  profilePicUrl,
  pageName = 'Page Name',
  caption = 'Primary text placeholder\u2026',
  imageUrl,
  tagline = 'Tagline placeholder\u2026',
  website = 'website.com',
  ctaText = 'Apply',
}: LinkedInMockupProps) {
  return (
    <div
      className="bg-white border border-[#e5e5e5] rounded-[3.89px] w-full mx-auto"
      style={{
        fontFamily: '"Segoe UI", sans-serif',
        filter: 'drop-shadow(0px 2.44px 6.72px rgba(0, 0, 0, 0.1))',
        maxWidth: '350px',
      }}
    >
      {/* Header - EXACT inbeat.co */}
      <div className="flex items-center justify-between" style={{ padding: '12px 12px 0px' }}>
        <div className="flex items-center" style={{ gap: '7px' }}>
          {/* Profile Picture - EXACT inbeat.co: no border, no border-radius */}
          <div
            className="bg-[#dfdfdf] flex items-center justify-center text-white font-bold overflow-hidden"
            style={{ width: '43px', height: '43px', fontSize: '20px' }}
          >
            {profilePicUrl ? (
              <img src={profilePicUrl} alt={pageName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-gray-500">{pageName.charAt(0)}</span>
            )}
          </div>
          {/* Page Name & Promoted - EXACT inbeat.co */}
          <div>
            <h4
              className="text-[#191919]"
              style={{
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '19px',
                letterSpacing: '0.015em',
                marginBottom: '3px',
              }}
            >
              {pageName}
            </h4>
            <div
              className="text-[#707070]"
              style={{
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.01em',
                fontWeight: 400,
              }}
            >
              Promoted
            </div>
          </div>
        </div>
        {/* Three-dot menu */}
        <button className="text-gray-600 hover:bg-gray-100 rounded p-1">
          <svg
            width="15"
            height="4"
            viewBox="0 0 15 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.92496 0.728516C2.35261 0.728516 2.76275 0.898398 3.06515 1.20079C3.36754 1.50319 3.53742 1.91332 3.53742 2.34097C3.53742 2.76862 3.36754 3.17876 3.06515 3.48115C2.76275 3.78355 2.35261 3.95343 1.92496 3.95343C1.49731 3.95343 1.08716 3.78355 0.784768 3.48115C0.482374 3.17876 0.3125 2.76862 0.3125 2.34097C0.3125 1.91332 0.482374 1.50319 0.784768 1.20079C1.08716 0.898398 1.49731 0.728516 1.92496 0.728516ZM7.29982 0.728516C7.72747 0.728516 8.13761 0.898398 8.44001 1.20079C8.7424 1.50319 8.91227 1.91332 8.91227 2.34097C8.91227 2.76862 8.7424 3.17876 8.44001 3.48115C8.13761 3.78355 7.72747 3.95343 7.29982 3.95343C6.87217 3.95343 6.46202 3.78355 6.15963 3.48115C5.85723 3.17876 5.68736 2.76862 5.68736 2.34097C5.68736 1.91332 5.85723 1.50319 6.15963 1.20079C6.46202 0.898398 6.87217 0.728516 7.29982 0.728516ZM12.6747 0.728516C13.1023 0.728516 13.5125 0.898398 13.8149 1.20079C14.1173 1.50319 14.2871 1.91332 14.2871 2.34097C14.2871 2.76862 14.1173 3.17876 13.8149 3.48115C13.5125 3.78355 13.1023 3.95343 12.6747 3.95343C12.247 3.95343 11.8369 3.78355 11.5345 3.48115C11.2321 3.17876 11.0622 2.76862 11.0622 2.34097C11.0622 1.91332 11.2321 1.50319 11.5345 1.20079C11.8369 0.898398 12.247 0.728516 12.6747 0.728516Z"
              fill="#262627"
            />
          </svg>
        </button>
      </div>

      {/* Caption - EXACT inbeat.co */}
      <div
        className="text-[#191919] break-words"
        style={{
          fontSize: '14px',
          lineHeight: '18px',
          letterSpacing: '-0.025em',
          fontWeight: 400,
          padding: '8px 12px',
          minHeight: '36px',
        }}
      >
        {caption}
      </div>

      {/* Post Image/Video */}
      <div
        className="relative bg-gray-200 flex items-center justify-center w-full"
        style={{ aspectRatio: adType === 'landscape' ? '16/9' : '1/1' }}
      >
        {imageUrl ? (
          <MediaDisplay url={imageUrl} alt="Ad preview" className="w-full h-full object-contain" />
        ) : (
          <div className="text-center text-gray-500">
            <div className="text-2xl font-bold">
              {adType === 'landscape' ? '16:9' : '1:1 or 4:5'}
            </div>
            <div className="text-xs mt-1">
              {adType === 'landscape' ? '1920x1080' : '1080x1080 or 1080x1350'}px recommended
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div
        className="flex items-center justify-between bg-[#eef3f8]"
        style={{ padding: '12px', border: 'none' }}
      >
        <div className="flex-1">
          <span
            className="block text-[#191919]"
            style={{
              fontWeight: 600,
              fontSize: '13px',
              lineHeight: '10px',
              letterSpacing: '0.035em',
            }}
          >
            {tagline}
          </span>
          <span
            className="block mt-1.5"
            style={{
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '10px',
              letterSpacing: '0.005em',
              color: 'rgba(0,0,0,0.6)',
            }}
          >
            {website}
          </span>
        </div>
        <button
          className="bg-transparent text-[#3072ac] border border-[#3072ac] rounded-[34px]"
          style={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px', padding: '3px 18px' }}
        >
          {ctaText}
        </button>
      </div>

      {/* Engagement Section */}
      <div
        className="flex items-center justify-between border-b border-[#dadde1]"
        style={{ padding: '12px 0px', margin: '0px 12px' }}
      >
        <div className="flex items-center" style={{ gap: '6px' }}>
          {/* Fixed React icons - Like, Celebrate, Love */}
          <div className="flex">
            {['like', 'celebrate', 'love'].map((reaction, idx) => (
              <img
                key={reaction}
                src={reactionIcons[reaction]}
                alt={reactionLabels[reaction]}
                className="w-5 h-5 rounded-full border-2 border-white"
                style={{ marginLeft: idx > 0 ? '-6px' : '0' }}
              />
            ))}
          </div>
          <span
            className="text-[#707070]"
            style={{ fontWeight: 400, fontSize: '13px', lineHeight: '17px' }}
          >
            15
          </span>
        </div>
        <div
          className="text-[#707070] text-right"
          style={{ fontWeight: 400, fontSize: '13px', lineHeight: '17px' }}
        >
          1 comment 1 share
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between" style={{ padding: '12px 8px' }}>
        <button
          className="flex items-center"
          style={{
            gap: '5px',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            color: '#707070',
          }}
        >
          <svg
            width="19"
            height="21"
            viewBox="0 0 19 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17.4604 10L13.5504 6.09002C12.7818 5.32038 12.2032 4.38224 11.8604 3.35002L11.3704 1.88002C11.1864 1.33317 10.8357 0.857718 10.3676 0.520503C9.8994 0.183287 9.33734 0.00126274 8.76038 1.82494e-05C8.39924 -0.00129736 8.04138 0.0685315 7.70724 0.205518C7.37309 0.342504 7.06919 0.543965 6.8129 0.798398C6.55661 1.05283 6.35294 1.35525 6.21353 1.6884C6.07412 2.02154 6.00169 2.37888 6.00038 2.74002V3.86002C6.00261 4.82832 6.15786 5.79018 6.46038 6.71002L6.89038 8.00002H2.12038C1.55812 8.00002 1.01889 8.22337 0.621312 8.62095C0.223735 9.01853 0.000378549 9.55776 0.000378549 10.12C0.00175879 10.4656 0.0860294 10.8058 0.246114 11.112C0.406199 11.4183 0.637426 11.6816 0.920379 11.88C0.637166 12.0734 0.40537 12.333 0.245063 12.6362C0.0847566 12.9394 0.00077077 13.2771 0.000378549 13.62C-0.00756786 14.0425 0.109749 14.4579 0.337539 14.8138C0.56533 15.1698 0.893396 15.4503 1.28038 15.62C1.10063 15.9229 1.00404 16.2678 1.00038 16.62C0.99951 17.1621 1.20632 17.6839 1.5783 18.0782C1.95028 18.4725 2.45917 18.7093 3.00038 18.74V18.88C3.00038 19.4423 3.22374 19.9815 3.62131 20.3791C4.01889 20.7767 4.55812 21 5.12038 21H12.6104C13.8524 20.9989 15.0775 20.7115 16.1904 20.16L16.5004 20H19.0004V10H17.4604ZM17.0004 18H16.0004L15.2704 18.37C14.4332 18.7816 13.5133 18.9971 12.5804 19H5.72038C5.49723 19.0089 5.27754 18.9428 5.0963 18.8123C4.91507 18.6819 4.78273 18.4945 4.72038 18.28L4.47038 17.41L3.62038 17C3.4246 16.9203 3.25914 16.7805 3.14774 16.6009C3.03635 16.4212 2.98476 16.2108 3.00038 16L3.17038 15L2.41038 14.26C2.24503 14.0985 2.14072 13.8848 2.1152 13.655C2.08967 13.4253 2.14451 13.1939 2.27038 13L2.93038 11.91L2.20038 10.81C2.15861 10.7593 2.12748 10.7006 2.10885 10.6376C2.09022 10.5745 2.08447 10.5084 2.09193 10.4431C2.0994 10.3777 2.11993 10.3146 2.1523 10.2574C2.18468 10.2002 2.22823 10.15 2.28038 10.11C2.37533 10.031 2.49711 9.99156 2.62038 10H9.67038L8.36038 6.08002C8.12151 5.36426 7.99994 4.61458 8.00038 3.86002V2.75002C8.00544 2.5527 8.08608 2.36486 8.22565 2.22529C8.36522 2.08572 8.55306 2.00508 8.75038 2.00002C8.90718 2.00014 9.06001 2.0494 9.18737 2.14088C9.31472 2.23236 9.4102 2.36146 9.46038 2.51002L10.0004 4.00002C10.4318 5.31493 11.1607 6.51263 12.1304 7.50002L16.6304 12H17.0004V18Z"
              fill="#707070"
            />
          </svg>
          <span>Like</span>
        </button>
        <button
          className="flex items-center"
          style={{
            gap: '5px',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            color: '#707070',
          }}
        >
          <svg
            width="22"
            height="19"
            viewBox="0 0 22 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 5.5H16V6.5H6V5.5ZM6 9.5H13V8.5H6V9.5ZM22 7.5C22.0148 8.59493 21.7643 9.67716 21.2697 10.6541C20.7751 11.6311 20.0512 12.4738 19.16 13.11L11 18.5V14.5H7C5.14348 14.5 3.36301 13.7625 2.05025 12.4497C0.737498 11.137 0 9.35652 0 7.5C0 5.64348 0.737498 3.86301 2.05025 2.55025C3.36301 1.2375 5.14348 0.5 7 0.5H15C16.8565 0.5 18.637 1.2375 19.9497 2.55025C21.2625 3.86301 22 5.64348 22 7.5ZM20 7.5C20 6.17392 19.4732 4.90215 18.5355 3.96447C17.5979 3.02678 16.3261 2.5 15 2.5H7C5.67392 2.5 4.40215 3.02678 3.46447 3.96447C2.52678 4.90215 2 6.17392 2 7.5C2 8.82608 2.52678 10.0979 3.46447 11.0355C4.40215 11.9732 5.67392 12.5 7 12.5H13V14.78L18 11.5C18.6336 11.0463 19.1469 10.4448 19.4955 9.74774C19.844 9.05069 20.0172 8.27913 20 7.5Z"
              fill="#707070"
            />
          </svg>
          <span>Comment</span>
        </button>
        <button
          className="flex items-center"
          style={{
            gap: '5px',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            color: '#707070',
          }}
        >
          <svg
            width="21"
            height="17"
            viewBox="0 0 21 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 7.5L16.39 14.5H14L18 8.5H6C5.48519 8.48939 4.97333 8.5803 4.49367 8.76756C4.014 8.95481 3.57593 9.23473 3.20448 9.59133C2.83302 9.94793 2.53546 10.3742 2.32879 10.8458C2.12212 11.3175 2.0104 11.8252 2 12.34V12.5C1.99772 12.9208 2.06184 13.3392 2.19 13.74L3.12 16.5H1L0.27 14.28C0.0908939 13.6831 -6.61168e-05 13.0632 3.60573e-08 12.44C0.0158096 10.8591 0.654923 9.34837 1.77843 8.23609C2.90194 7.12382 4.41904 6.49992 6 6.5H18L14 0.5H16.39L21 7.5Z"
              fill="#707070"
            />
          </svg>
          <span>Share</span>
        </button>
        <button
          className="flex items-center"
          style={{
            gap: '5px',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: '18px',
            color: '#707070',
          }}
        >
          <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M21 0L0 7L7.66 11.26L16 5L9.74 13.34L14 21L21 0Z" fill="#707070" />
          </svg>
          <span>Send</span>
        </button>
      </div>
    </div>
  );
}
